export type TournamentFeature =
  | "fixtures"
  | "scoring"
  | "playerStats"
  | "standings"
  | "ties"
  | "content";

export interface FixtureParticipant {
  slot: number;
  team?: string | null;
  players?: string[];
}

export interface SportAdapter {
  sport: string;
  fixtureShape: "participants" | "teams" | "sides";
  minParticipants: number;
  minPlayersPerSide?: number;
  maxPlayersPerSide?: number;
  features: TournamentFeature[];
}

const tierOneFeatures: TournamentFeature[] = [
  "fixtures",
  "scoring",
  "playerStats",
  "standings",
  "content",
];

const adapters: Record<string, SportAdapter> = {
  football: { sport: "Football", fixtureShape: "teams", minParticipants: 2, features: tierOneFeatures },
  cricket: { sport: "Cricket", fixtureShape: "participants", minParticipants: 2, features: tierOneFeatures },
  tennis: { sport: "Tennis", fixtureShape: "participants", minParticipants: 2, maxPlayersPerSide: 2, features: tierOneFeatures },
  badminton: { sport: "Badminton", fixtureShape: "participants", minParticipants: 2, maxPlayersPerSide: 2, features: [...tierOneFeatures, "ties"] },
  tabletennis: { sport: "TableTennis", fixtureShape: "participants", minParticipants: 2, maxPlayersPerSide: 2, features: tierOneFeatures },
  pickleball: { sport: "Pickleball", fixtureShape: "participants", minParticipants: 2, maxPlayersPerSide: 2, features: tierOneFeatures },
  padel: { sport: "Padel", fixtureShape: "participants", minParticipants: 2, features: tierOneFeatures },
  basketball: { sport: "Basketball", fixtureShape: "participants", minParticipants: 2, minPlayersPerSide: 2, features: tierOneFeatures },
  chess: { sport: "Chess", fixtureShape: "participants", minParticipants: 2, features: tierOneFeatures },
  swimming: { sport: "Swimming", fixtureShape: "participants", minParticipants: 2, features: tierOneFeatures },
  skating: { sport: "Skating", fixtureShape: "participants", minParticipants: 2, features: tierOneFeatures },
};

const genericAdapter: SportAdapter = {
  sport: "Generic",
  fixtureShape: "participants",
  minParticipants: 2,
  features: ["fixtures"],
};

const normalizeSportKey = (sport: string) => sport.replace(/[\s_-]/g, "").toLowerCase();

export const getSportAdapter = (sport: string): SportAdapter =>
  adapters[normalizeSportKey(sport)] || { ...genericAdapter, sport };

export const getUnsupportedTournamentFeatures = (adapter: SportAdapter): TournamentFeature[] =>
  (["scoring", "playerStats", "standings", "ties", "content"] as TournamentFeature[]).filter(
    (feature) => !adapter.features.includes(feature)
  );

export const validateFixtureParticipants = (
  adapter: SportAdapter,
  participants: FixtureParticipant[]
): string | undefined => {
  if (participants.length < adapter.minParticipants) {
    return `At least ${adapter.minParticipants} participants are required.`;
  }

  if (adapter.fixtureShape === "teams" && participants.some((participant) => !participant.team)) {
    return `${adapter.sport} requires a team on each side.`;
  }

  if (adapter.minPlayersPerSide || adapter.maxPlayersPerSide) {
    for (const participant of participants) {
      if (participant.team) continue;
      const playerCount = participant.players?.length || 0;
      if (adapter.minPlayersPerSide && playerCount < adapter.minPlayersPerSide) {
        return `${adapter.sport} requires exactly two players on each side.`;
      }
      if (adapter.maxPlayersPerSide && playerCount > adapter.maxPlayersPerSide) {
        return `${adapter.sport} allows at most ${adapter.maxPlayersPerSide} players on each side.`;
      }
    }
  }

  return undefined;
};

const asPlayerList = (value: unknown): string[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value as string];
};

const legacyParticipants = (payload: Record<string, any>): FixtureParticipant[] => {
  if (Array.isArray(payload.participants)) {
    return payload.participants.map((participant, index) => ({
      slot: participant.slot ?? index + 1,
      ...(participant.team ? { team: participant.team } : {}),
      ...(participant.players?.length ? { players: asPlayerList(participant.players) } : {}),
    }));
  }

  return [
    payload.side1 || { team: payload.teamA || null, players: asPlayerList(payload.playerA) },
    payload.side2 || { team: payload.teamB || null, players: asPlayerList(payload.playerB) },
  ].map((participant, index) => ({
    slot: index + 1,
    ...(participant.team ? { team: participant.team } : {}),
    ...(participant.players?.length ? { players: asPlayerList(participant.players) } : {}),
  }));
};

export const buildFixturePayload = (sport: string, payload: Record<string, any>) => {
  const adapter = getSportAdapter(sport);
  const participants = legacyParticipants(payload);
  const {
    teamA,
    teamB,
    playerA,
    playerB,
    participants: _participants,
    side1: _side1,
    side2: _side2,
    ...common
  } = payload;

  if (adapter.fixtureShape === "teams") {
    return {
      ...common,
      ...(teamA || participants[0]?.team ? { teamA: teamA || participants[0]?.team } : {}),
      ...(teamB || participants[1]?.team ? { teamB: teamB || participants[1]?.team } : {}),
    };
  }

  return {
    ...common,
    participants,
  };
};
